import { useEffect, useState, createContext, useContext } from "react";
import { api } from "./api";

const SettingsCtx = createContext({ settings: null, refresh: () => {} });

/**
 * Apply dynamic head/theme tokens from settings:
 *  - <title>, <meta description>, <meta og:*>
 *  - favicon + apple-touch-icon
 *  - <meta theme-color>
 *  - CSS custom properties (brand-*, theme-*, radius/shadow scales, fonts)
 */
function applyDynamicTheme(s) {
  if (!s) return;

  // --- Head metadata ---
  const companyName = (s.company_name && s.company_name !== "[Votre Entreprise]" ? s.company_name : "").trim();
  const sector = (s.sector || "").trim();
  // Phase 33m : on HUB pages, force WECOCKPIT title (don't use the demo pod settings)
  const path = (typeof window !== "undefined") ? window.location.pathname : "";
  const isHubPath = (
    s.is_hub === true ||
    path === "/" ||
    path.startsWith("/cockpit-features") ||
    path.startsWith("/realisations-cockpit") ||
    path.startsWith("/agency") ||
    path.startsWith("/admin") ||
    path.startsWith("/mon-espace") ||
    path.startsWith("/onboarding") ||
    path.startsWith("/devis-client") ||
    path === "/site"
  );
  let finalTitle;
  if (isHubPath) {
    const agency = (s.agency_brand_name || "").trim();
    // Sur le site public WECOCKPIT, on garde la marque produit. Sur /admin et /agency on
    // affiche le nom de l'agence pour personnaliser le back-office.
    if (path === "/" || path.startsWith("/cockpit-features")) {
      finalTitle = "WECOCKPIT — Pilotons ensemble votre présence digitale";
    } else if (path.startsWith("/realisations-cockpit")) {
      finalTitle = "Sites réalisés — WECOCKPIT";
    } else if (path.startsWith("/agency")) {
      finalTitle = `Hub agence${agency ? " — " + agency : ""}`;
    } else if (path.startsWith("/admin")) {
      finalTitle = `Back-office${agency ? " — " + agency : ""}`;
    } else if (path.startsWith("/mon-espace")) {
      finalTitle = "Mon espace — WECOCKPIT";
    } else if (path.startsWith("/onboarding")) {
      finalTitle = "Démarrer un projet — WECOCKPIT";
    } else if (path.startsWith("/devis-client")) {
      finalTitle = "Mon devis — WECOCKPIT";
    } else {
      finalTitle = "WECOCKPIT";
    }
  } else {
    const titleParts = [];
    if (companyName) titleParts.push(companyName);
    if (sector) titleParts.push(sector);
    finalTitle = titleParts.length ? titleParts.join(" — ") : "Site en configuration";
  }
  if (document.title !== finalTitle) document.title = finalTitle;

  // Description
  const desc = s.meta_description || s.footer_description || sector || "Site propulsé par MSC";
  let metaDesc = document.querySelector('meta[name="description"]');
  if (metaDesc) metaDesc.setAttribute("content", (desc || "").slice(0, 200));

  // theme-color (address bar mobile)
  let themeMeta = document.querySelector('meta[name="theme-color"]');
  if (themeMeta && s.primary_color) themeMeta.setAttribute("content", s.primary_color);

  // Favicon + apple-touch-icon
  // Sur le back-office (admin/agency) on affiche le logo DigiPilote (artefact officiel).
  // Sur le site public on garde le logo IPHB.
  const fav = document.getElementById("app-favicon");
  const apple = document.getElementById("app-apple-icon");
  const DIGIPILOTE_FAVICON = "https://customer-assets.emergentagent.com/job_d6b30d32-0560-4729-9096-ffa84a0b3e19/artifacts/t0fx03n8_digipilote_logo.png";

  if (isHubPath) {
    // Back-office DigiPilote : favicon dédié
    if (fav) { fav.setAttribute("href", DIGIPILOTE_FAVICON); fav.setAttribute("type", "image/png"); }
    if (apple) apple.setAttribute("href", DIGIPILOTE_FAVICON);
  } else {
    // Pod: use site logo
    const logo = s.logo_url && s.logo_url !== "" ? s.logo_url : "";
    if (logo) {
      const isSvg = logo.toLowerCase().endsWith(".svg");
      if (fav) { fav.setAttribute("href", logo); fav.setAttribute("type", isSvg ? "image/svg+xml" : "image/png"); }
      if (apple) apple.setAttribute("href", logo);
    } else if (s.primary_color) {
      // Generate a colored dot favicon matching the brand color
      const hex = s.primary_color.replace("#", "");
      const svg = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Ccircle cx='12' cy='12' r='10' fill='%23${hex}'/%3E%3C/svg%3E`;
      if (fav) { fav.setAttribute("href", svg); fav.setAttribute("type", "image/svg+xml"); }
      if (apple) apple.setAttribute("href", svg);
    }
  }

  // Open Graph site name
  const og = document.getElementById("og-site-name");
  if (og) og.setAttribute("content", companyName || "Votre Entreprise");

  // --- CSS custom properties (design tokens) ---
  const root = document.documentElement.style;
  if (s.primary_color) root.setProperty("--brand-navy", s.primary_color);
  if (s.secondary_color) root.setProperty("--brand-sky", s.secondary_color);
  if (s.accent_color) root.setProperty("--brand-red", s.accent_color);
  if (s.background_color) root.setProperty("--theme-bg", s.background_color);
  if (s.surface_color) root.setProperty("--theme-surface", s.surface_color);
  if (s.text_color) root.setProperty("--theme-text", s.text_color);
  if (s.muted_color) root.setProperty("--theme-muted", s.muted_color);
  if (s.radius_scale != null) root.setProperty("--theme-radius-scale", String(s.radius_scale));
  if (s.shadow_scale != null) root.setProperty("--theme-shadow-scale", String(s.shadow_scale));
  if (s.spacing_scale != null) root.setProperty("--theme-spacing-scale", String(s.spacing_scale));
  if (s.font_display) root.setProperty("--font-display", `"${s.font_display}", system-ui, sans-serif`);
  if (s.font_body) root.setProperty("--font-body", `"${s.font_body}", system-ui, sans-serif`);
}

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
      applyDynamicTheme(data);
    } catch (e) {
      console.warn("settings load failed", e);
    }
  };

  useEffect(() => { refresh(); }, []);

  // Phase 33m : re-apply title on route change (hub vs pod aware)
  useEffect(() => {
    let last = window.location.pathname;
    const tick = () => {
      if (window.location.pathname !== last) {
        last = window.location.pathname;
        if (settings) applyDynamicTheme(settings);
      }
    };
    const id = setInterval(tick, 400);
    return () => clearInterval(id);
  }, [settings]);

  // --- Theme Studio : preview en iframe via postMessage ---
  // Le parent (ThemeStudioAdmin) envoie {type:'preview-theme', theme:{...}} → on applique les tokens.
  useEffect(() => {
    const handler = (event) => {
      const msg = event.data;
      if (!msg || typeof msg !== "object") return;
      if (msg.type === "preview-theme" && msg.theme) {
        try { applyDynamicTheme({ ...(settings || {}), ...msg.theme }); } catch { /* ignore */ }
      }
      if (msg.type === "preview-theme-reset") {
        try { if (settings) applyDynamicTheme(settings); } catch { /* ignore */ }
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [settings]);

  return <SettingsCtx.Provider value={{ settings, refresh }}>{children}</SettingsCtx.Provider>;
};


export const useSettings = () => useContext(SettingsCtx);

export function isOpenNow(hours = []) {
  // Always returns a POSITIVE message — never "Fermé" standalone.
  const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
  const now = new Date();
  const todayIdx = (now.getDay() + 6) % 7; // Monday=0
  const mins = now.getHours() * 60 + now.getMinutes();

  const parseHM = (t) => {
    if (!t) return null;
    const [h, m] = t.split(":").map(Number);
    if (Number.isNaN(h)) return null;
    return h * 60 + (m || 0);
  };
  const slots = (dayData) => {
    if (!dayData || dayData.closed) return [];
    const out = [];
    for (const key of ["morning", "afternoon"]) {
      const range = dayData[key];
      if (!range) continue;
      const [a, b] = range.split("-").map((s) => s.trim());
      const sa = parseHM(a); const sb = parseHM(b);
      if (sa != null && sb != null && sb > sa) out.push({ start: sa, end: sb, label: a });
    }
    return out.sort((x, y) => x.start - y.start);
  };
  const fmt = (m) => {
    const h = Math.floor(m / 60); const mm = m % 60;
    return mm ? `${h}h${String(mm).padStart(2, "0")}` : `${h}h`;
  };

  // 1) Open right now ?
  const todaySlots = slots(hours[todayIdx]);
  for (const s of todaySlots) {
    if (mins >= s.start && mins <= s.end) return { open: true, label: "Ouvert maintenant" };
  }

  // 2) Later today ?
  const laterToday = todaySlots.find((s) => s.start > mins);
  if (laterToday) return { open: false, label: `Ouvre à ${fmt(laterToday.start)}` };

  // 3) Next open day (search 1..7 ahead)
  for (let i = 1; i <= 7; i++) {
    const idx = (todayIdx + i) % 7;
    const next = slots(hours[idx]);
    if (!next.length) continue;
    const dayLabel = i === 1 ? "demain" : DAYS[idx].toLowerCase();
    return { open: false, label: `Ouvre ${dayLabel} à ${fmt(next[0].start)}` };
  }

  // 4) Fallback — no hours configured
  return { open: false, label: "Sur rendez-vous" };
}
